import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { ToastService } from '../../../common/services/toast-service/toast-service.service';
import { IonicColor } from '../../../common/entities/toast/ionic-color';
import { LoggingService } from '../../../logging/logging.service';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';

export enum CurafidaConnectionState {
    NONE,
    UNKNOWN,
    BACKEND_REACHABLE,
    BACKEND_UNREACHABLE,
    ONLINE,
    OFFLINE,
}

@Injectable({
    providedIn: 'root',
})
export class NetworkService {
    private readonly log = inject(LoggingService).getLogger(this.constructor.name);
    private readonly onlineStatus = new BehaviorSubject<CurafidaConnectionState>(CurafidaConnectionState.UNKNOWN);

    constructor(private readonly toastService: ToastService) {
        this.initService();
    }

    private async initService(): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            this.initializeNetworkEventsForCapacitor();
        } else {
            this.initializeNetworkEventsForBrowser();
        }
        const connection = await Network.getStatus();
        const onlineStatus =
            connection.connectionType !== 'none' ? CurafidaConnectionState.ONLINE : CurafidaConnectionState.OFFLINE;
        this.onlineStatus.next(onlineStatus);
    }

    private initializeNetworkEventsForCapacitor(): void {
        Network.addListener('networkStatusChange', (connectionStatus) => {
            if (!connectionStatus.connected) {
                this.updateNetworkStatus(CurafidaConnectionState.OFFLINE);
            } else {
                this.updateNetworkStatus(CurafidaConnectionState.ONLINE);
            }
        });
    }

    public onNetworkChange(): Observable<CurafidaConnectionState> {
        return this.onlineStatus.asObservable();
    }

    public getCurrentNetworkStatus(): CurafidaConnectionState {
        return this.onlineStatus.getValue();
    }

    public isCurrentNetworkOnline(): boolean {
        return this.getCurrentNetworkStatus() === CurafidaConnectionState.ONLINE;
    }

    async hasLowNetworkQuality(slowConnection: string = '2g'): Promise<boolean> {
        if (!Capacitor.isNativePlatform()) {
            return false; // Experimental Technology, not compatible with all kind of browser https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection
        }
        const connectionType = await this.getConnectionType();
        return slowConnection === connectionType;
    }

    private initializeNetworkEventsForBrowser(): void {
        fromEvent(window, 'offline').subscribe(() => {
            this.updateNetworkStatus(CurafidaConnectionState.OFFLINE);
        });
        fromEvent(window, 'online').subscribe(() => {
            this.updateNetworkStatus(CurafidaConnectionState.ONLINE);
        });
    }

    private updateNetworkStatus(status: CurafidaConnectionState): void {
        this.onlineStatus.next(status);
        const connection = status === CurafidaConnectionState.OFFLINE ? 'Offline' : 'Online';
        if (connection === 'Offline') {
            this.toastService.showToast(`Sie befinden sich aktuell im ${connection}-Modus.`, IonicColor.medium, 4000);
        }
    }

    private async getConnectionType(): Promise<string> {
        if (Capacitor.isNativePlatform()) {
            const status = await Network.getStatus();
            this.log.debug('Download', status.connectionType);
            return status.connectionType;
        }
    }
}
